import { useAuth } from "@clerk/react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ApiError } from "@/features/api/apiClient";
import {
  adjustAdminUserAccess,
  getAdminUserDetail,
  type AdminAccessAdjustmentAction,
  type AdminUserDetailResponse,
  type AdminUserSummary,
} from "@/features/admin/adminApi";

function formatDate(value: string | null): string {
  if (!value) {
    return "Not set";
  }

  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(value));
}

function formatTier(user: AdminUserSummary): string {
  if (user.tier === "pro") {
    return "Pro";
  }

  if (user.is_lifetime) {
    return "Lifetime Standard";
  }

  return user.tier.charAt(0).toUpperCase() + user.tier.slice(1);
}

function formatToolName(value: string): string {
  if (value === "launch_readiness_checker") {
    return "Launch Readiness Checker";
  }

  if (value === "monetisation_model_suggester") {
    return "Monetisation Model Suggester";
  }

  if (value === "sales_page_headline_generator") {
    return "Sales Page Headline Generator";
  }

  return value.replace(/_/g, " ");
}

type AccessActionOption = {
  value: AdminAccessAdjustmentAction;
  label: string;
};

const ACCESS_ACTION_LABELS: Record<AdminAccessAdjustmentAction, string> = {
  grant_standard: "Grant Standard",
  grant_lifetime_standard: "Grant Lifetime Standard",
  grant_pro: "Grant Pro",
  revoke_standard: "Revoke Standard",
  revoke_lifetime_standard: "Revoke Lifetime Standard",
  revoke_pro: "Revoke Pro",
  reset_to_free: "Reset to Free",
};

const FREE_ACTIONS: AdminAccessAdjustmentAction[] = [
  "grant_standard",
  "grant_lifetime_standard",
  "grant_pro",
];

const STANDARD_ACTIONS: AdminAccessAdjustmentAction[] = [
  "grant_lifetime_standard",
  "grant_pro",
  "revoke_standard",
  "reset_to_free",
];

const LIFETIME_STANDARD_ACTIONS: AdminAccessAdjustmentAction[] = [
  "grant_pro",
  "revoke_lifetime_standard",
  "reset_to_free",
];

const PRO_BASE_ACTIONS: AdminAccessAdjustmentAction[] = [
  "revoke_pro",
  "reset_to_free",
];

const PRO_BACKUP_ACCESS_LABELS: Partial<Record<AdminAccessAdjustmentAction, string>> = {
  grant_standard: "Add Standard backup access",
  grant_lifetime_standard: "Add Lifetime Standard backup access",
  revoke_standard: "Remove Standard backup access",
  revoke_lifetime_standard: "Remove Lifetime Standard backup access",
};

const REASON_MIN_LENGTH = 5;

function actionNeedsStandardDuration(action: AdminAccessAdjustmentAction) {
  return action === "grant_standard";
}

function actionNeedsProDuration(action: AdminAccessAdjustmentAction) {
  return action === "grant_pro";
}

function hasStandardWindow(user: AdminUserSummary) {
  return Boolean(user.standard_expiry_date);
}

function isProUser(detail: AdminUserDetailResponse) {
  return detail.access.effectiveTier === "pro";
}

function hasLifetimeBackup(detail: AdminUserDetailResponse) {
  return detail.access.isLifetimeStandard || detail.user.is_lifetime;
}

function hasStandardBackup(detail: AdminUserDetailResponse) {
  return hasStandardWindow(detail.user);
}

function getProBackupOutcome(detail: AdminUserDetailResponse) {
  if (hasLifetimeBackup(detail)) {
    return "Lifetime Standard";
  }

  if (hasStandardBackup(detail)) {
    return "Standard";
  }

  return "Free";
}

function getAccessActionLabel(
  detail: AdminUserDetailResponse,
  action: AdminAccessAdjustmentAction,
) {
  if (isProUser(detail)) {
    if (action === "revoke_pro") {
      const outcome = getProBackupOutcome(detail);
      return outcome === "Free"
        ? "Revoke Pro and return to Free"
        : `Revert from Pro to ${outcome}`;
    }

    if (action === "revoke_lifetime_standard") {
      return "Keep Pro, remove Lifetime backup access";
    }

    if (action === "revoke_standard") {
      return "Keep Pro, remove Standard backup access";
    }

    if (PRO_BACKUP_ACCESS_LABELS[action]) {
      return PRO_BACKUP_ACCESS_LABELS[action];
    }
  }

  return ACCESS_ACTION_LABELS[action];
}

function getAccessActionOptions(
  detail: AdminUserDetailResponse,
): AccessActionOption[] {
  const hasLifetimeStandard = hasLifetimeBackup(detail);
  const hasStandardAccessWindow = hasStandardWindow(detail.user);
  const hasStandardFallback = hasLifetimeStandard || hasStandardAccessWindow;
  const actions: AdminAccessAdjustmentAction[] = [];

  if (detail.access.effectiveTier === "free") {
    actions.push(...FREE_ACTIONS);
  } else if (detail.access.effectiveTier === "standard") {
    actions.push(
      ...(hasLifetimeStandard ? LIFETIME_STANDARD_ACTIONS : STANDARD_ACTIONS),
    );
  } else {
    if (!hasStandardFallback) {
      actions.push("grant_standard", "grant_lifetime_standard");
    } else if (!hasLifetimeStandard) {
      actions.push("grant_lifetime_standard");
    }

    if (hasStandardAccessWindow && !hasLifetimeStandard) {
      actions.push("revoke_standard");
    }

    if (hasLifetimeStandard) {
      actions.push("revoke_lifetime_standard");
    }

    actions.push(...PRO_BASE_ACTIONS);
  }

  return actions.map((value) => ({
    value,
    label: getAccessActionLabel(detail, value),
  }));
}

function isProBackupGrantAction(action: AdminAccessAdjustmentAction) {
  return action === "grant_standard" || action === "grant_lifetime_standard";
}

function isProBackupRemovalAction(action: AdminAccessAdjustmentAction) {
  return action === "revoke_standard" || action === "revoke_lifetime_standard";
}

function getActionWarning(
  detail: AdminUserDetailResponse,
  action: AdminAccessAdjustmentAction,
): string | null {
  if (isProUser(detail) && isProBackupGrantAction(action)) {
    return "Because this user is already Pro, this will not change their current visible plan. It gives them backup Standard access if Pro is later revoked or lapses.";
  }

  if (isProUser(detail) && isProBackupRemovalAction(action)) {
    if (action === "revoke_lifetime_standard") {
      return "This user will stay on Pro. If Pro is later removed or lapses, they will no longer return to Lifetime Standard.";
    }

    return "This user will stay on Pro. If Pro is later removed or lapses, they will no longer return to Standard from this access window.";
  }

  if (isProUser(detail) && action === "revoke_pro") {
    const outcome = getProBackupOutcome(detail);

    if (outcome === "Lifetime Standard") {
      return "This will remove Pro access now. Because the user has Lifetime Standard backup access, their visible plan will become Lifetime Standard.";
    }

    if (outcome === "Standard") {
      return "This will remove Pro access now. Because the user has Standard backup access, their visible plan will become Standard.";
    }

    return "This will remove Pro access now. The user will return to the Free monthly blueprint allowance.";
  }

  if (isProUser(detail) && action === "reset_to_free") {
    return "This removes Pro and all paid backup access. The user will return to the Free monthly blueprint allowance.";
  }

  if (action === "revoke_pro") {
    return "Revoking Pro may hide Pro tools, Pro blueprint sections, PDF export, and saved Pro tool result history unless the user has another active entitlement.";
  }

  if (action === "revoke_lifetime_standard") {
    return "Revoking Lifetime Standard removes the user's permanent Standard fallback. If they do not have Pro or another valid Standard entitlement, they may return to Free.";
  }

  if (action === "revoke_standard") {
    return "Revoking Standard removes this user's Standard access window. If no higher entitlement applies, they may return to Free.";
  }

  if (action === "reset_to_free") {
    return "Resetting this user to Free removes paid access entitlements and returns them to the Free monthly blueprint allowance.";
  }

  return "This will update product access only. It does not process a payment.";
}

function getExpectedResult(
  detail: AdminUserDetailResponse,
  action: AdminAccessAdjustmentAction,
): string {
  if (isProUser(detail)) {
    if (isProBackupGrantAction(action) || isProBackupRemovalAction(action)) {
      return "Expected result: User stays Pro";
    }

    if (action === "revoke_pro") {
      return `Expected result: User becomes ${getProBackupOutcome(detail)}`;
    }

    if (action === "reset_to_free") {
      return "Expected result: User becomes Free";
    }
  }

  if (action === "grant_pro") {
    return "Expected result: User becomes Pro";
  }

  if (action === "grant_lifetime_standard") {
    return "Expected result: User becomes Lifetime Standard";
  }

  if (action === "grant_standard") {
    return "Expected result: User becomes Standard";
  }

  if (action === "reset_to_free") {
    return "Expected result: User becomes Free";
  }

  if (action === "revoke_lifetime_standard" || action === "revoke_standard") {
    return "Expected result: User becomes Free";
  }

  return "Expected result: User access is recalculated";
}

function getReasonValidationError(reason: string): string | null {
  if (reason.trim().length === 0) {
    return "Reason is required.";
  }

  if (reason.trim().length < REASON_MIN_LENGTH) {
    return "Reason must be at least 5 characters.";
  }

  return null;
}

function SafeNotFound() {
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
      <p className="text-sm font-semibold text-slate-500">Not found</p>
      <h2 className="mt-2 text-2xl font-semibold tracking-normal text-slate-950">
        User detail is not available
      </h2>
      <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600">
        The requested admin view could not be opened.
      </p>
    </section>
  );
}

export function AdminUserDetail() {
  const { id } = useParams<{ id: string }>();
  const { getToken, isLoaded } = useAuth();
  const [detail, setDetail] = useState<AdminUserDetailResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isNotFound, setIsNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [action, setAction] =
    useState<AdminAccessAdjustmentAction>("grant_standard");
  const [standardDurationDays, setStandardDurationDays] = useState("183");
  const [proDurationDays, setProDurationDays] = useState("");
  const [reason, setReason] = useState("");
  const [isReasonTouched, setIsReasonTouched] = useState(false);
  const [hasAttemptedAdjustment, setHasAttemptedAdjustment] = useState(false);
  const [isConfirmingAdjustment, setIsConfirmingAdjustment] = useState(false);
  const [isAdjustingAccess, setIsAdjustingAccess] = useState(false);
  const [adjustmentError, setAdjustmentError] = useState<string | null>(null);
  const [adjustmentSuccess, setAdjustmentSuccess] = useState<string | null>(null);

  useEffect(() => {
    async function loadDetail() {
      if (!isLoaded || !id) {
        return;
      }

      setIsLoading(true);
      setIsNotFound(false);
      setError(null);

      try {
        const token = await getToken();
        setDetail(await getAdminUserDetail(token, id));
      } catch (err) {
        if (
          err instanceof ApiError &&
          (err.status === 401 || err.status === 403 || err.status === 404)
        ) {
          setIsNotFound(true);
          return;
        }

        setError(
          err instanceof Error
            ? err.message
            : "Unable to load user detail right now.",
        );
      } finally {
        setIsLoading(false);
      }
    }

    void loadDetail();
  }, [getToken, id, isLoaded]);

  useEffect(() => {
    if (!detail) {
      return;
    }

    const options = getAccessActionOptions(detail);

    if (options.length > 0 && !options.some((option) => option.value === action)) {
      setAction(options[0].value);
      setIsConfirmingAdjustment(false);
    }
  }, [action, detail]);

  if (isLoading) {
    return <div className="h-80 animate-pulse rounded-lg bg-white" />;
  }

  if (isNotFound || !detail) {
    return <SafeNotFound />;
  }

  const countCards = [
    { label: "Projects", value: detail.counts.projects },
    { label: "Blueprints", value: detail.counts.blueprints },
    { label: "Generated", value: detail.counts.generatedBlueprints },
    { label: "Tool results", value: detail.counts.toolResults },
  ];
  const actionOptions = getAccessActionOptions(detail);
  const selectedActionLabel =
    actionOptions.find((option) => option.value === action)?.label ??
    ACCESS_ACTION_LABELS[action];
  const actionWarning = getActionWarning(detail, action);
  const expectedResult = getExpectedResult(detail, action);
  const reasonValidationError =
    isReasonTouched || hasAttemptedAdjustment
      ? getReasonValidationError(reason)
      : null;

  async function submitAccessAdjustment() {
    if (!id) {
      return;
    }

    const trimmedReason = reason.trim();
    const nextReasonError = getReasonValidationError(reason);

    setHasAttemptedAdjustment(true);

    if (nextReasonError) {
      setAdjustmentError(null);
      return;
    }

    const options: { standardDurationDays?: number; proDurationDays?: number } = {};

    if (actionNeedsStandardDuration(action) && standardDurationDays.trim()) {
      options.standardDurationDays = Number(standardDurationDays);
    }

    if (actionNeedsProDuration(action) && proDurationDays.trim()) {
      options.proDurationDays = Number(proDurationDays);
    }

    setIsAdjustingAccess(true);
    setAdjustmentError(null);
    setAdjustmentSuccess(null);

    try {
      const token = await getToken();
      const updated = await adjustAdminUserAccess(token, id, {
        action,
        reason: trimmedReason,
        options,
      });
      setDetail(updated);
      setReason("");
      setIsReasonTouched(false);
      setHasAttemptedAdjustment(false);
      setIsConfirmingAdjustment(false);
      setAdjustmentSuccess("Access adjustment applied.");
    } catch (err) {
      setAdjustmentError(
        err instanceof Error
          ? err.message
          : "Unable to apply access adjustment right now.",
      );
    } finally {
      setIsAdjustingAccess(false);
    }
  }

  return (
    <div className="space-y-6">
      <Link
        to="/admin/users"
        className="inline-flex text-sm font-semibold text-indigo-700 hover:text-indigo-900"
      >
        Back to Users
      </Link>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {error}
        </div>
      ) : null}

      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <p className="text-sm font-semibold text-indigo-700">User detail</p>
        <div className="mt-2 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h2 className="break-words text-2xl font-semibold tracking-normal text-slate-950">
              {detail.user.name}
            </h2>
            <p className="mt-2 break-words text-sm text-slate-600">
              {detail.user.email}
            </p>
          </div>
          {detail.user.is_admin ? (
            <span className="w-fit rounded-full bg-indigo-100 px-3 py-1 text-sm font-semibold text-indigo-700">
              Admin
            </span>
          ) : null}
        </div>
      </section>

      <section className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="font-semibold text-slate-950">Access state</h3>
          <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-slate-500">Current plan</dt>
              <dd className="font-semibold text-slate-950">
                {formatTier(detail.user)}
              </dd>
            </div>
            <div>
              <dt className="text-slate-500">Pro status</dt>
              <dd className="font-semibold capitalize text-slate-950">
                {detail.user.pro_subscription_status}
              </dd>
            </div>
            <div>
              <dt className="text-slate-500">Blueprint usage</dt>
              <dd className="font-semibold text-slate-950">
                {detail.access.usage.monthlyBlueprintCount} of{" "}
                {detail.access.usage.monthlyBlueprintLimit}
              </dd>
            </div>
            <div>
              <dt className="text-slate-500">Monthly reset</dt>
              <dd className="font-semibold text-slate-950">
                {formatDate(detail.user.monthly_reset_date)}
              </dd>
            </div>
          </dl>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="font-semibold text-slate-950">Access adjustment</h3>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            Manual access changes should be used carefully. They do not process
            payments or refunds. They only update product access inside Alchemy
            Blueprint Engine.
          </p>
          <div className="mt-4 grid gap-4">
            <label className="grid gap-2 text-sm font-semibold text-slate-700">
              Action
              <select
                value={action}
                onChange={(event) => {
                  setAction(event.target.value as AdminAccessAdjustmentAction);
                  setIsConfirmingAdjustment(false);
                  setAdjustmentError(null);
                  setAdjustmentSuccess(null);
                }}
                className="rounded-md border border-slate-300 px-3 py-2 text-sm font-normal text-slate-950 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
              >
                {actionOptions.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>

            {actionNeedsStandardDuration(action) ? (
              <label className="grid gap-2 text-sm font-semibold text-slate-700">
                Standard duration days
                <input
                  type="number"
                  min="1"
                  max="3660"
                  value={standardDurationDays}
                  onChange={(event) => setStandardDurationDays(event.target.value)}
                  className="rounded-md border border-slate-300 px-3 py-2 text-sm font-normal text-slate-950 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                />
              </label>
            ) : null}

            {actionNeedsProDuration(action) ? (
              <label className="grid gap-2 text-sm font-semibold text-slate-700">
                Pro duration days
                <input
                  type="number"
                  min="1"
                  max="3660"
                  placeholder="Leave blank for ongoing access"
                  value={proDurationDays}
                  onChange={(event) => setProDurationDays(event.target.value)}
                  className="rounded-md border border-slate-300 px-3 py-2 text-sm font-normal text-slate-950 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                />
              </label>
            ) : null}

            <label className="grid gap-2 text-sm font-semibold text-slate-700">
              Reason
              <textarea
                value={reason}
                maxLength={500}
                rows={4}
                onChange={(event) => {
                  setReason(event.target.value);
                  setIsReasonTouched(true);
                  setIsConfirmingAdjustment(false);
                  setAdjustmentSuccess(null);
                  if (!getReasonValidationError(event.target.value)) {
                    setAdjustmentError(null);
                  }
                }}
                className="resize-y rounded-md border border-slate-300 px-3 py-2 text-sm font-normal text-slate-950 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
              />
            </label>

            {reasonValidationError ? (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                {reasonValidationError}
              </div>
            ) : null}

            {actionWarning ? (
              <div
                className={
                  action.startsWith("revoke") || action === "reset_to_free"
                    ? "rounded-md border border-amber-200 bg-amber-50 p-3 text-sm leading-6 text-amber-900"
                    : "rounded-md border border-slate-200 bg-slate-50 p-3 text-sm leading-6 text-slate-600"
                }
              >
                {actionWarning}
              </div>
            ) : null}

            <p className="rounded-md border border-indigo-100 bg-indigo-50 p-3 text-sm font-semibold text-indigo-900">
              {expectedResult}
            </p>

            {adjustmentError ? (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                {adjustmentError}
              </div>
            ) : null}

            {adjustmentSuccess ? (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                {adjustmentSuccess}
              </div>
            ) : null}

            {isConfirmingAdjustment ? (
              <div className="rounded-md border border-slate-300 bg-slate-50 p-3">
                <p className="text-sm leading-6 text-slate-700">
                  This will apply {selectedActionLabel.toLowerCase()} to this
                  user. This action should only be used for support, beta
                  access, or correcting entitlement issues.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => void submitAccessAdjustment()}
                    disabled={isAdjustingAccess}
                    className="rounded-md bg-indigo-700 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-800 disabled:cursor-not-allowed disabled:bg-slate-400"
                  >
                    {isAdjustingAccess ? "Applying..." : "Confirm adjustment"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsConfirmingAdjustment(false)}
                    disabled={isAdjustingAccess}
                    className="rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-white disabled:cursor-not-allowed disabled:text-slate-400"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setHasAttemptedAdjustment(true);
                  setAdjustmentError(null);
                  setAdjustmentSuccess(null);
                  setIsConfirmingAdjustment(!getReasonValidationError(reason));
                }}
                className="rounded-md bg-slate-950 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-slate-800"
              >
                Apply access adjustment
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {countCards.map((card) => (
          <div
            key={card.label}
            className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm"
          >
            <p className="text-sm text-slate-500">{card.label}</p>
            <p className="mt-2 text-2xl font-semibold text-slate-950">
              {card.value}
            </p>
          </div>
        ))}
      </section>

      <section className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="font-semibold text-slate-950">Recent projects</h3>
          <div className="mt-4 grid gap-3">
            {detail.recentProjects.map((project) => (
              <div key={project.id} className="rounded-md bg-slate-50 p-3">
                <p className="break-words text-sm font-semibold text-slate-950">
                  {project.name}
                </p>
                <p className="mt-1 text-xs capitalize text-slate-500">
                  {project.status.replace("_", " ")} - {formatDate(project.updated_at)}
                </p>
              </div>
            ))}
            {detail.recentProjects.length === 0 ? (
              <p className="text-sm text-slate-500">No projects yet.</p>
            ) : null}
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="font-semibold text-slate-950">Recent blueprints</h3>
          <div className="mt-4 grid gap-3">
            {detail.recentBlueprints.map((blueprint) => (
              <div key={blueprint.id} className="rounded-md bg-slate-50 p-3">
                <p className="break-words text-sm font-semibold text-slate-950">
                  {blueprint.title}
                </p>
                <p className="mt-1 text-xs capitalize text-slate-500">
                  {blueprint.status.replace("_", " ")} -{" "}
                  {blueprint.generation_mode} - {formatDate(blueprint.updated_at)}
                </p>
              </div>
            ))}
            {detail.recentBlueprints.length === 0 ? (
              <p className="text-sm text-slate-500">No blueprints yet.</p>
            ) : null}
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="font-semibold text-slate-950">Recent tool results</h3>
          <div className="mt-4 grid gap-3">
            {detail.recentToolResults.map((result) => (
              <div key={result.id} className="rounded-md bg-slate-50 p-3">
                <p className="break-words text-sm font-semibold text-slate-950">
                  {formatToolName(result.tool_type)}
                </p>
                <p className="mt-1 text-xs capitalize text-slate-500">
                  {result.status} - {formatDate(result.created_at)}
                </p>
              </div>
            ))}
            {detail.recentToolResults.length === 0 ? (
              <p className="text-sm text-slate-500">No tool results yet.</p>
            ) : null}
          </div>
        </div>
      </section>
    </div>
  );
}
